import { NgFor, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output
} from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { NgChartsModule } from 'ng2-charts';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IconComponent, TypographyComponent } from 'components';
import {
	GetSocialListeningMyBrandAnalyticsInfluencersByMediaTypeService,
	MediaTypeInfluencer
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-influencers-by-media-type.service';
import {
	ChartWidget,
	PreviewSlide,
	ReportDateRange,
	SummaryTextWidget,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import {
	applyContentItemOverrides,
	hasPositiveTotal,
	platformDisplayName
} from '../../../../services/report-preview-utils';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportBlockRender } from '../../../../services/report-blocks.service';
import {
	ReportSectionChartSwitch,
	sectionChartSource,
	sectionChartSwitch
} from '../../../../services/report-chart-types';
import { ReportChartSwitchComponent } from '../../components/report-chart-switch/report-chart-switch.component';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';

// Categorical palette from the report kit (report-tokens dot colors), applied
// by rank so the largest channel takes the first swatch — matching the Figma
// pie + Channel-breakdown table exactly.
const REPORT_PALETTE = [
	'#f0a3c6', // pink
	'#2872e1', // accent blue
	'#e94f6a', // red
	'#a98cf0', // violet
	'#6cd6c8', // teal
	'#f5c451', // amber
	'#9aa3bb' // slate
];

function paletteColor(index: number): string {
	return REPORT_PALETTE[index % REPORT_PALETTE.length];
}

export interface MediaTypeTableRow {
	id: string;
	displayName: string;
	color: string;
	count: number;
	percent: string;
	avgMentions: string;
}

@Component({
	standalone: true,
	selector: 'app-influencers-by-media-type-slide',
	templateUrl: './influencers-by-media-type-slide.component.html',
	styleUrls: ['./influencers-by-media-type-slide.component.scss'],
	imports: [
		NgFor,
		NgIf,
		IconComponent,
		TypographyComponent,
		NgChartsModule,
		SlideNoDataComponent,
		ReportInlineTextComponent,
		ReportChartSwitchComponent,
		ReportSlideBrandComponent
	]
})
export class InfluencersByMediaTypeSlideComponent
	implements OnChanges, OnDestroy
{
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();
	/** The reader picked another type for this section's chart. */
	@Output() chartTypeChange = new EventEmitter<ReportBlockRender | null>();

	/** The heading and summary the reader can type into, in the editor. */
	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	title = '';
	titleColor = '#2872e1';
	dateLabel = '';
	summaryText = '';

	topSharePct = '';
	topShareLabel = '';

	chartData: ChartConfiguration<'pie'>['data'] | null = null;
	readonly chartOptions: ChartConfiguration<'pie'>['options'] = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				callbacks: {
					label: (ctx: any) => ` ${ctx.parsed} influencers`
				}
			}
		}
	};
	readonly chartType = 'pie' as const;
	tableRows: MediaTypeTableRow[] = [];
	/** What the chart is drawn as: its own design until the reader switches. */
	chartSwitch: ReportSectionChartSwitch = sectionChartSwitch(
		'influencers_by_media_type',
		null,
		null
	);

	private readonly destroy$ = new Subject<void>();
	private cachedPlatforms: MediaTypeInfluencer[] | null = null;
	private cacheKey: string | null = null;

	get isEmpty(): boolean {
		return (
			!this.cachedPlatforms ||
			!hasPositiveTotal(
				this.cachedPlatforms.map(platform => platform.count)
			)
		);
	}

	constructor(
		private readonly analyticsService: GetSocialListeningMyBrandAnalyticsInfluencersByMediaTypeService
	) {}

	ngOnChanges(): void {
		this.destroy$.next();
		if (!this.item || !this.brandId || !this.dateRange) return;

		const currentCacheKey = `${this.brandId}:${this.dateRange.start}:${this.dateRange.end}`;
		if (this.cachedPlatforms && this.cacheKey === currentCacheKey) {
			this.applyPlatforms(this.cachedPlatforms);
			return;
		}

		this.analyticsService
			.fetch(
				this.brandId,
				new Date(this.dateRange.start),
				undefined,
				new Date(this.dateRange.end)
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe(platforms => {
				this.cachedPlatforms = platforms;
				this.cacheKey = currentCacheKey;
				this.applyPlatforms(platforms);
			});
	}

	private applyPlatforms(platforms: MediaTypeInfluencer[]): void {
		const sorted = [...platforms].sort((a, b) => b.count - a.count);
		const rawSlide = this.buildSlide(sorted);
		const slide = applyContentItemOverrides(
			rawSlide,
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide, sorted);
		this.slideBuilt.emit(slide);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildSlide(platforms: MediaTypeInfluencer[]): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;
		const labels = platforms.map(platform =>
			platformDisplayName(platform.platform)
		);
		const colors = platforms.map((_, index) => paletteColor(index));

		const chartWidget: ChartWidget = {
			id: `${prefix}_chart`,
			kind: 'chart',
			visible: true,
			content: {
				chartType: 'pie',
				dataSourceKey: 'influencers_by_platform',
				seriesMeta: platforms.map((platform, index) => ({
					key: platform.platform.toLowerCase(),
					label: labels[index],
					color: colors[index]
				})),
				chartJsConfig: {
					type: 'pie',
					data: {
						labels,
						datasets: [
							{
								data: platforms.map(platform => platform.count),
								backgroundColor: colors,
								borderColor: '#ffffff',
								borderWidth: 2
							}
						]
					}
				}
			}
		};

		return {
			id: `${prefix}_slide`,
			slideType: 'influencers_by_media_type',
			title: this.item.title || 'Influencers by Media Type',
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: {
						text: this.item.title || 'Influencers by Media Type',
						color: '#2872e1'
					}
				},
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				},
				{
					id: `${prefix}_summary`,
					kind: 'summary_text',
					visible: true,
					content: { text: this.item.description || '' }
				},
				chartWidget
			]
		};
	}

	private updateViewModel(
		slide: PreviewSlide,
		platforms: MediaTypeInfluencer[]
	): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);

		const titleWidget = slide.widgets.find(
			widget => widget.kind === 'title' && !hidden.has(widget.id)
		) as TitleWidget | undefined;
		const dateWidget = slide.widgets.find(
			widget => widget.kind === 'date_range' && !hidden.has(widget.id)
		) as TitleWidget | undefined;
		const summaryWidget = slide.widgets.find(
			widget => widget.kind === 'summary_text' && !hidden.has(widget.id)
		) as SummaryTextWidget | undefined;

		this.title = titleWidget?.content.text ?? slide.title;
		this.titleColor = titleWidget?.content.color ?? '#2872e1';
		this.dateLabel =
			dateWidget?.content.text ?? slide.dateRange?.label ?? '';
		this.summaryText = summaryWidget?.content.text ?? '';

		const total = platforms.reduce(
			(sum, platform) => sum + platform.count,
			0
		);
		const labels = platforms.map(platform =>
			platformDisplayName(platform.platform)
		);

		const chartWidget = slide.widgets.find(
			widget => widget.kind === 'chart' && !hidden.has(widget.id)
		) as ChartWidget | undefined;

		const resolvedColors =
			chartWidget?.content.seriesMeta?.map(
				(series, index) => series.color ?? paletteColor(index)
			) ?? platforms.map((_, index) => paletteColor(index));

		this.chartData = {
			labels,
			datasets: [
				{
					data: platforms.map(platform => platform.count),
					backgroundColor: resolvedColors,
					borderColor: '#ffffff',
					borderWidth: 2
				}
			]
		};

		this.tableRows = platforms.map((platform, index) => ({
			id: platform.platform.toLowerCase(),
			displayName: labels[index],
			color: resolvedColors[index],
			count: platform.count,
			percent:
				total > 0
					? `${Math.round((platform.count / total) * 100)}%`
					: '0%',
			// No avg-mentions-per-influencer field on MediaTypeInfluencer.
			avgMentions: '—'
		}));

		const topRow = this.tableRows[0];
		this.topSharePct = topRow?.percent ?? '';
		this.topShareLabel = topRow ? `${topRow.displayName} share` : '';

		// A channel per label, in the colour the breakdown beside it uses.
		this.chartSwitch = sectionChartSwitch(
			this.item.id,
			this.item.chartType,
			sectionChartSource(this.item.id, {
				labels,
				series: [
					{
						key: 'influencers',
						label: 'Influencers',
						values: platforms.map(platform => platform.count)
					}
				],
				labelColors: resolvedColors,
				labelColumn: 'Channel'
			})
		);
	}

	trackByRowId(_index: number, row: MediaTypeTableRow): string {
		return row.id;
	}
}
